import PropTypes from "prop-types";
import clsx from "clsx";
import { Button } from "@/components/utils/Button";
import { Eyebrow } from "@/components/utils/Eyebrow";
import { usePadding } from "@/components/utils/Padding";
import "./styles.scss";

/* #META
 {
  "NAME": "Project Headline",
  "CLASSNAME": "project-headline-section",
  "URL": "http://nicasource.local"
 }
#ENDMETA */

//image sizing
const SIZE_TABLE = {
  sm: { w: { min: 86, pref: 198, max: 198 }, h: { min: 86, pref: 198, max: 198 } },
  md: { w: { min: 120, pref: 314, max: 314 }, h: { min: 120, pref: 314, max: 314 } },
  lg: { w: { min: 180, pref: 430, max: 430 }, h: { min: 180, pref: 430, max: 430 } },
};

const PRESET_TEXT_WIDTH = {
  sm: "lg:w-4/5",
  md: "lg:w-[70%]",
  lg: "lg:w-3/5",
};

const ProjectHeadline = ({ data }) => {
  const {
    // Padding Component
    padding_component: paddingComponent = {},
    // Eyebrow Component
    eyebrow_component: eyebrowComponent = {},

    content_info: contentInfo = {},
    display_image: displayImage = false,
    image_size: preset = "sm",
    image,
  } = data ?? {};

  const {
    headline: headline,
    // Button Component
    button_component: buttonComponent = {},
  } = contentInfo ?? {};

  const paddingStyle = usePadding(paddingComponent);

  const size = SIZE_TABLE[preset] || SIZE_TABLE.sm;
  const textWidthClass = PRESET_TEXT_WIDTH[preset] || PRESET_TEXT_WIDTH.sm;

  // Build clamp() strings for width & height
  const widthClamp = `clamp(${size.w.min}px, ${size.w.pref}px, ${size.w.max}px)`;
  const heightClamp = `clamp(${size.h.min}px, ${size.h.pref}px, ${size.h.max}px)`;
  const hasImage = displayImage && !!image?.url;

  // Only provide CSS vars when an image is shown
  const containerStyle = hasImage
    ? {
        ["--ph-img-w"]: widthClamp,
        ["--ph-img-h"]: heightClamp,
        ["--ph-min-h"]: heightClamp,
      }
    : undefined;

  return (
    <section className={clsx("project-headline-section")} style={paddingStyle}>
      <div className="wrapper-container container-revamp">
        {/* Eyebrow */}
        <Eyebrow {...eyebrowComponent} />

        <div className={clsx("inner-container", hasImage && "has-image")} style={containerStyle}>
          <div className={clsx("text-content", displayImage && textWidthClass)}>
            {headline && (
              <div className="headline" dangerouslySetInnerHTML={{ __html: headline }} />
            )}

            {/* Button */}
            <Button {...buttonComponent} />
          </div>

          {displayImage && image?.url && (
            <div className="image">
              <img src={image.url} alt={image.alt} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

ProjectHeadline.propTypes = {
  data: PropTypes.shape({
    padding_component: PropTypes.oneOfType([PropTypes.bool, PropTypes.any]),
    display_image: PropTypes.bool,
    image: PropTypes.shape({
      url: PropTypes.string,
      alt: PropTypes.string,
    }),
    image_size: PropTypes.oneOf(["sm", "md", "lg"]),

    // Group: content_info
    content_info: PropTypes.shape({
      headline: PropTypes.string,
      display_button: PropTypes.bool,
      cta_button: PropTypes.shape({
        url: PropTypes.string,
        title: PropTypes.string,
        target: PropTypes.string,
      }),
      button_position: PropTypes.oneOf(["start", "center", "end"]),
    }),
  }),
};

export default ProjectHeadline;
